import React, { useEffect, useMemo, useRef, useState } from 'react';
import './OurPlatformTravelerCarousel.css';
import MaskedVideoFrame from './MaskedVideoFrame';
import carouselData from './ourPlatformTravelerCarouselData.json';
import sunSeekingTraveler from 'url:../../assets/redesign/gifs/LeisureTraveler.mp4';
import BusinessTraveler from 'url:../../assets/redesign/gifs/BusinessTraveler.mp4';
import FamilyTraveler from 'url:../../assets/redesign/gifs/FamilyTraveler.mp4';
import InternationalTraveler from 'url:../../assets/redesign/gifs/InternationalTraveler.mp4';
import LuxuryTraveler from 'url:../../assets/redesign/gifs/LuxuryTraveler.mp4';
import UpcomingTripTraveler from 'url:../../assets/redesign/gifs/UpcomingTripTraveler.mp4';
import wellnessTravelerPng from 'url:../../assets-optimized/redesign/gifs/wellnessTraveler.png';
import wellnessTravelerWebp from 'url:../../assets-optimized/redesign/gifs/wellnessTraveler.webp';
import wellnessTravelerAvif from 'url:../../assets-optimized/redesign/gifs/wellnessTraveler.avif';

const videoMap = {
  'LeisureTraveler.mp4': sunSeekingTraveler,
  'BusinessTraveler.mp4': BusinessTraveler,
  'FamilyTraveler.mp4': FamilyTraveler,
  'InternationalTraveler.mp4': InternationalTraveler,
  'LuxuryTraveler.mp4': LuxuryTraveler,
  'UpcomingTripTraveler.mp4': UpcomingTripTraveler,
};

const imageMap = {
  'wellnessTraveler.png': {
    fallback: wellnessTravelerPng,
    webp: wellnessTravelerWebp,
    avif: wellnessTravelerAvif,
  },
};

const OurPlatformTravelerCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const stepperRef = useRef(null);
  const isFirstRender = useRef(true);

  const slides = useMemo(
    () =>
      carouselData.map((slide) => ({
        ...slide,
        videoSrc: videoMap[slide.videoAsset],
        image: imageMap[slide.imageAsset],
      })),
    []
  );
  const total = slides.length;
  const activeSlide = slides[activeIndex];

  const preloadSources = useMemo(
    () => [...new Set(slides.map((slide) => slide.videoSrc).filter(Boolean))],
    [slides]
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // On mobile, keep the stepper (and the active step) in view when the slide changes
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (window.innerWidth > 768) return;
    const track = stepperRef.current;
    if (!track) return;

    // Center the active step within the horizontally-scrolling track. Only the
    // track scrolls horizontally — the page is never scrolled vertically.
    const activeEl = track.querySelector('.od-traveler-step.is-active');
    if (activeEl) {
      const target = activeEl.offsetLeft - (track.clientWidth - activeEl.clientWidth) / 2;
      track.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    }
  }, [activeIndex]);

  const handlePrev = () => setActiveIndex((i) => (i === 0 ? total - 1 : i - 1));
  const handleNext = () => setActiveIndex((i) => (i === total - 1 ? 0 : i + 1));

  return (
    <section className='od-traveler-carousel-section'>
      <div className='od-traveler-preload' aria-hidden='true'>
        {preloadSources.map((videoSrc) => (
          <video
            key={videoSrc}
            muted
            playsInline
            preload='auto'
            className='od-traveler-preload-video'
          >
            <source src={videoSrc} type='video/mp4' />
          </video>
        ))}
      </div>

      <div className='sr-only' aria-live='polite'>
        {`Slide ${activeIndex + 1} of ${total}: ${activeSlide?.title || ''}`}
      </div>

      <div className='od-traveler-shell'>
        <div className='od-traveler-stage'>
          <button
            className='od-traveler-arrow od-traveler-arrow--prev'
            onClick={handlePrev}
            aria-label='Previous persona'
          >
            <svg
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='1.8'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <line x1='19' y1='12' x2='5' y2='12'></line>
              <polyline points='12 19 5 12 12 5'></polyline>
            </svg>
          </button>

          <div className='od-traveler-card' key={activeSlide.id}>
            <MaskedVideoFrame
              topText={activeSlide.topText}
              mainText={activeSlide.title}
              videoSrc={activeSlide.videoSrc}
              imageSrc={activeSlide.image?.fallback}
              imageWebp={activeSlide.image?.webp}
              imageAvif={activeSlide.image?.avif}
              maskId={`od-travel-mask-${activeSlide.id}`}
              className='od-traveler-card-frame'
              videoContainerClassName='od-traveler-card-video-wrap'
              mobileTallLayout={isMobile}
              isActive
            >
              <div className='od-traveler-card-overlay'>
                <div className='od-traveler-points-bar'>
                  {activeSlide.points.map((point, pointIdx) => (
                    <div key={pointIdx} className='od-traveler-point'>
                      <span className='od-traveler-point-dot' aria-hidden='true'></span>
                      <span className='od-traveler-point-text'>{point}</span>
                    </div>
                  ))}
                </div>
              </div>
            </MaskedVideoFrame>
          </div>

          <button
            className='od-traveler-arrow od-traveler-arrow--next'
            onClick={handleNext}
            aria-label='Next persona'
          >
            <svg
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='1.8'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <line x1='5' y1='12' x2='19' y2='12'></line>
              <polyline points='12 5 19 12 12 19'></polyline>
            </svg>
          </button>
        </div>

        <div className='od-traveler-stepper-track' ref={stepperRef}>
          <div className='od-traveler-stepper' role='tablist' aria-label='Select traveler persona'>
            {slides.map((slide, index) => (
              <button
                key={slide.id}
                type='button'
                role='tab'
                aria-selected={index === activeIndex}
                className={`od-traveler-step ${index === activeIndex ? 'is-active' : ''}`}
                onClick={() => setActiveIndex(index)}
              >
                <span className='od-traveler-step-dot' aria-hidden='true'></span>
                <span className='od-traveler-step-label'>{slide.stepLabel}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default OurPlatformTravelerCarousel;
